import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Anchor, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { OverviewCards, LinkBlock } from "../../components/affiliate/AffOverview";
import { CustomersTable, EarningsChart, PayoutsTab } from "../../components/affiliate/AffTables";
import { SettingsTab } from "../../components/affiliate/AffSettings";
import { affApi, affToken } from "../../lib/affApi";
import { fmtErr } from "../../lib/api";

export default function AffiliateDashboard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await affApi.get("/affiliate/dashboard");
      setData(d); setErr(null);
    } catch (e) {
      if (e?.response?.status === 401) { affToken.clear(); nav("/affiliate/login", { replace: true }); return; }
      setErr(fmtErr(e));
    }
  }, [nav]);

  useEffect(() => {
    if (!affToken.get()) { nav("/affiliate/login", { replace: true }); return; }
    load();
  }, [load, nav]);

  useEffect(() => {
    if (params.get("welcome") && data) toast.success("Welcome aboard — your referral link is ready.");
  }, [params, data]);

  const logout = () => { affToken.clear(); nav("/affiliate/login", { replace: true }); };

  if (err) return <div className="min-h-screen grid place-items-center text-sm text-red-600" data-testid="aff-dashboard-error">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500" data-testid="aff-dashboard-loading">Loading your dashboard…</div>;

  const { affiliate, cards, customers, chart, payouts } = data;
  return (
    <div className="min-h-screen bg-zinc-50 text-ink" data-testid="aff-dashboard">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/affiliate" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center"><Anchor className="h-4 w-4" /></div>
            <span className="font-display text-[17px] font-medium tracking-tight">Seavanto</span>
            <span className="text-xs text-emerald-700 font-semibold tracking-widest uppercase ml-1 hidden sm:inline">Partners</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-zinc-600" data-testid="aff-header-name">{affiliate.name}</span>
            {affiliate.status === "suspended" && <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs" data-testid="aff-suspended-badge">Suspended</span>}
            <button onClick={load} className="text-zinc-500 hover:text-ink" title="Refresh" data-testid="aff-refresh"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={logout} className="inline-flex items-center gap-1 text-zinc-600 hover:text-ink" data-testid="aff-logout"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-6 space-y-5">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl">Your earnings</h1>
          <p className="text-sm text-zinc-600 mt-1">{affiliate.rate_pct}% of each referred customer's subscription for 12 months. Accruals are written on the 1st of each month.</p>
        </div>
        <OverviewCards cards={cards} ratePct={affiliate.rate_pct} />
        <LinkBlock code={affiliate.code} link={affiliate.link} />
        <Tabs defaultValue="customers">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="customers" data-testid="aff-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="aff-tab-earnings">Earnings</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="aff-tab-payouts">Payouts</TabsTrigger>
            <TabsTrigger value="settings" data-testid="aff-tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="customers" className="mt-4"><CustomersTable customers={customers} /></TabsContent>
          <TabsContent value="earnings" className="mt-4"><EarningsChart data={chart} /></TabsContent>
          <TabsContent value="payouts" className="mt-4"><PayoutsTab payouts={payouts} cards={cards} /></TabsContent>
          <TabsContent value="settings" className="mt-4"><SettingsTab affiliate={affiliate} onSaved={(a) => setData((d) => ({ ...d, affiliate: { ...d.affiliate, ...a } }))} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
